import { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';
import {
    useFileEmployeeReportMutation,
    useGetMyEmployeeReportsQuery,
    useGetMyInvestigationsQuery,
} from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import {
    CATEGORY_LABELS,
    CATEGORY_OPTIONS,
    SEVERITY_OPTIONS,
    SEVERITY_PILL,
    STATUS_LABELS,
    STATUS_PILL,
} from '@/utils/employeeReports';
import type { EmployeeReportCategory, EmployeeReportSeverity, FileEmployeeReportRequest } from '@/types';

type Mode = 'investigation' | 'standalone';

function formatDate(value?: string): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const inputClass =
    'w-full rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15';

export default function EmployeeReports() {
    const [mode, setMode] = useState<Mode>('investigation');
    const [investigationId, setInvestigationId] = useState('');
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [nin, setNin] = useState('');
    const [category, setCategory] = useState<EmployeeReportCategory>('fraud');
    const [severity, setSeverity] = useState<EmployeeReportSeverity>('medium');
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const { data: investigations, isLoading: loadingInvestigations } = useGetMyInvestigationsQuery();
    const { data: myReports, isLoading: loadingReports } = useGetMyEmployeeReportsQuery();
    const [fileReport, { isLoading: isSubmitting }] = useFileEmployeeReportMutation();

    // Only paid/started investigations carry a verified identity worth reporting from.
    const reportableInvestigations = useMemo(
        () => (investigations ?? []).filter((inv) => inv.isFormSubmitted || inv.employeeConsentGiven),
        [investigations],
    );

    const resetForm = () => {
        setInvestigationId('');
        setFullName('');
        setEmail('');
        setPhone('');
        setNin('');
        setCategory('fraud');
        setSeverity('medium');
        setDescription('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!description.trim()) {
            setError('Please describe what happened.');
            return;
        }

        let body: FileEmployeeReportRequest;
        if (mode === 'investigation') {
            if (!investigationId) {
                setError('Select the investigation this report relates to.');
                return;
            }
            body = { investigationId, category, severity, description: description.trim() };
        } else {
            if (!fullName.trim()) {
                setError("Enter the employee's full name.");
                return;
            }
            if (!email.trim() && !phone.trim() && !nin.trim()) {
                setError('Provide at least one of email, phone or NIN so the person can be matched.');
                return;
            }
            body = {
                employeeFullName: fullName.trim(),
                category,
                severity,
                description: description.trim(),
            };
            if (email.trim()) body.employeeEmail = email.trim();
            if (phone.trim()) body.employeePhone = phone.trim();
            if (nin.trim()) body.employeeNin = nin.trim();
        }

        try {
            await fileReport(body).unwrap();
            setSuccess('Report filed. Our team will review it.');
            resetForm();
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to file report. Please try again.'));
        }
    };

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6 w-full">
                    <div>
                        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">Report an employee</h1>
                        <p className="mt-2 text-sm text-[#4e4d4d] max-w-2xl">
                            File a report about an employee for serious misconduct. Reports are reviewed by our team and
                            help protect other employers from repeat offenders.
                        </p>
                    </div>

                    {/* File a report */}
                    <form
                        onSubmit={handleSubmit}
                        className="rounded-2xl border border-[#eee] p-4 md:p-6 flex flex-col gap-5"
                    >
                        <div className="flex items-center gap-2">
                            <ShieldAlert className="h-5 w-5 text-[#FF6B35]" />
                            <h2 className="text-lg font-bold text-[#1b1b1b]">File a new report</h2>
                        </div>

                        {/* Mode toggle */}
                        <div className="inline-flex w-full max-w-md rounded-lg border border-[#e0e0e0] p-1 text-sm">
                            <button
                                type="button"
                                onClick={() => setMode('investigation')}
                                className={`flex-1 rounded-md px-3 py-2 font-medium transition-colors ${
                                    mode === 'investigation' ? 'bg-[#151515] text-white' : 'text-[#4e4d4d]'
                                }`}
                            >
                                From my investigation
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode('standalone')}
                                className={`flex-1 rounded-md px-3 py-2 font-medium transition-colors ${
                                    mode === 'standalone' ? 'bg-[#151515] text-white' : 'text-[#4e4d4d]'
                                }`}
                            >
                                Someone else
                            </button>
                        </div>

                        {mode === 'investigation' ? (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-[#1b1b1b]">Investigation</label>
                                <select
                                    value={investigationId}
                                    onChange={(e) => setInvestigationId(e.target.value)}
                                    className={inputClass}
                                    disabled={loadingInvestigations}
                                >
                                    <option value="">
                                        {loadingInvestigations ? 'Loading…' : 'Select an investigation'}
                                    </option>
                                    {reportableInvestigations.map((inv) => (
                                        <option key={inv.id} value={inv.id}>
                                            {inv.employeeFirstName} {inv.employeeLastName}
                                            {inv.supportCode ? ` · ${inv.supportCode}` : ''}
                                        </option>
                                    ))}
                                </select>
                                <p className="mt-1 text-xs text-[#717680]">
                                    We pull the verified identity (including NIN) from the selected investigation.
                                </p>
                                {!loadingInvestigations && reportableInvestigations.length === 0 && (
                                    <p className="mt-1 text-xs text-amber-700">
                                        No eligible investigations yet — use “Someone else” to report a standalone
                                        identity.
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="mb-1 block text-sm font-medium text-[#1b1b1b]">
                                        Full name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        placeholder="Jane Doe"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-[#1b1b1b]">Email</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="jane@example.com"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-[#1b1b1b]">Phone</label>
                                    <input
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="08012345678"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-[#1b1b1b]">NIN</label>
                                    <input
                                        value={nin}
                                        onChange={(e) => setNin(e.target.value)}
                                        placeholder="12345678901"
                                        className={inputClass}
                                    />
                                </div>
                                <div className="flex items-end">
                                    <p className="text-xs text-[#717680]">
                                        Provide at least one of email, phone or NIN.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-[#1b1b1b]">Category</label>
                                <select
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value as EmployeeReportCategory)}
                                    className={inputClass}
                                >
                                    {CATEGORY_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium text-[#1b1b1b]">Severity</label>
                                <select
                                    value={severity}
                                    onChange={(e) => setSeverity(e.target.value as EmployeeReportSeverity)}
                                    className={inputClass}
                                >
                                    {SEVERITY_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-medium text-[#1b1b1b]">
                                What happened? <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={4}
                                placeholder="Describe the incident factually…"
                                className={inputClass}
                            />
                        </div>

                        {error && (
                            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                {error}
                            </div>
                        )}
                        {success && (
                            <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                                {success}
                            </div>
                        )}

                        <div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="inline-flex items-center gap-2 rounded-lg bg-[#151515] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#2a2a2a] disabled:opacity-60"
                            >
                                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                {isSubmitting ? 'Filing…' : 'File report'}
                            </button>
                        </div>
                    </form>

                    {/* My reports */}
                    <div className="rounded-2xl border border-[#eee] p-4 md:p-6">
                        <h2 className="mb-4 text-lg font-bold text-[#1b1b1b]">My reports</h2>
                        {loadingReports ? (
                            <div className="flex justify-center py-10">
                                <Loader2 className="h-6 w-6 animate-spin text-[#FF6B35]" />
                            </div>
                        ) : !myReports || myReports.length === 0 ? (
                            <p className="py-8 text-center text-sm text-[#9aa0a6]">
                                You haven’t filed any reports yet.
                            </p>
                        ) : (
                            <ul className="flex flex-col gap-3">
                                {myReports.map((r) => (
                                    <li key={r.id} className="rounded-xl border border-[#f0f0f0] p-4">
                                        <div className="mb-1 flex flex-wrap items-center gap-2">
                                            <span className="text-sm font-semibold text-[#1b1b1b]">
                                                {r.employeeFullName}
                                            </span>
                                            <span className="text-xs text-[#717680]">
                                                {CATEGORY_LABELS[r.category] ?? r.category}
                                            </span>
                                            <span
                                                className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SEVERITY_PILL[r.severity]}`}
                                            >
                                                {r.severity}
                                            </span>
                                            <span
                                                className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_PILL[r.status]}`}
                                            >
                                                {STATUS_LABELS[r.status] ?? r.status}
                                            </span>
                                            <span className="ml-auto text-xs text-[#717680]">
                                                {formatDate(r.createdAt)}
                                            </span>
                                        </div>
                                        <p className="text-sm text-[#4e4d4d] whitespace-pre-wrap">{r.description}</p>
                                        {r.reviewNotes && (
                                            <p className="mt-2 rounded-lg bg-[#fafafa] px-3 py-2 text-xs text-[#4e4d4d]">
                                                <span className="font-semibold">Review notes:</span> {r.reviewNotes}
                                            </p>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}
